import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Newspaper, ExternalLink, Clock, ArrowLeft, Search, Filter,
  TrendingUp, Cpu, Brain, Sparkles, RefreshCw
} from 'lucide-react';
import WebsiteFooter from '@/components/website/WebsiteFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AINewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const categories = [
    { id: 'all', name: 'All News', icon: Newspaper },
    { id: 'llm', name: 'LLMs & GPT', icon: Brain },
    { id: 'enterprise', name: 'Enterprise AI', icon: TrendingUp },
    { id: 'tech', name: 'AI Technology', icon: Cpu },
    { id: 'innovation', name: 'Innovation', icon: Sparkles },
  ];

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-news`);
      setNews(response.data.articles || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch news:', error);
      // Fallback news
      setNews([
        {
          id: "1",
          title: "OpenAI Unveils GPT-5 with Revolutionary Reasoning Capabilities",
          description: "The latest iteration of OpenAI's language model demonstrates unprecedented problem-solving abilities and multimodal understanding, setting new benchmarks in AI performance.",
          source: "TechCrunch",
          category: "llm",
          publishedAt: new Date().toISOString(),
          url: "https://techcrunch.com",
          image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800"
        },
        {
          id: "2",
          title: "AI-Powered Code Generation Transforms Software Development",
          description: "New AI tools are enabling developers to write code 10x faster while maintaining quality and security standards across enterprise applications.",
          source: "Wired",
          category: "tech",
          publishedAt: new Date(Date.now() - 86400000).toISOString(),
          url: "https://wired.com",
          image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800"
        },
        {
          id: "3",
          title: "Enterprise AI Adoption Reaches Record High in 2026",
          description: "A comprehensive survey reveals 78% of Fortune 500 companies now use AI in core business operations, up from 52% last year.",
          source: "Forbes",
          category: "enterprise",
          publishedAt: new Date(Date.now() - 172800000).toISOString(),
          url: "https://forbes.com",
          image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800"
        },
        {
          id: "4",
          title: "AI in Healthcare: Machine Learning Detects Diseases Earlier",
          description: "Revolutionary diagnostic AI systems can now identify potential health issues up to 5 years before traditional methods with 95% accuracy.",
          source: "MIT Technology Review",
          category: "innovation",
          publishedAt: new Date(Date.now() - 259200000).toISOString(),
          url: "https://technologyreview.com",
          image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800"
        },
        {
          id: "5",
          title: "Autonomous AI Agents Are Reshaping Business Operations",
          description: "Companies are deploying AI agents that can independently handle complex workflows, decision-making, and customer interactions at scale.",
          source: "Bloomberg",
          category: "enterprise",
          publishedAt: new Date(Date.now() - 345600000).toISOString(),
          url: "https://bloomberg.com",
          image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800"
        },
        {
          id: "6",
          title: "The Rise of AI-Native Startups: $50B Invested in 2025",
          description: "Venture capital continues to pour into companies built entirely around artificial intelligence capabilities, reaching record investment levels.",
          source: "VentureBeat",
          category: "innovation",
          publishedAt: new Date(Date.now() - 432000000).toISOString(),
          url: "https://venturebeat.com",
          image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800"
        },
        {
          id: "7",
          title: "Google DeepMind Achieves Breakthrough in Protein Folding",
          description: "AlphaFold 3 can now predict protein structures with near-perfect accuracy, accelerating drug discovery and biotechnology research.",
          source: "Nature",
          category: "innovation",
          publishedAt: new Date(Date.now() - 518400000).toISOString(),
          url: "https://nature.com",
          image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800"
        },
        {
          id: "8",
          title: "Claude 4 Sets New Standard for AI Safety and Alignment",
          description: "Anthropic's latest model demonstrates significant improvements in following ethical guidelines while maintaining high performance.",
          source: "The Verge",
          category: "llm",
          publishedAt: new Date(Date.now() - 604800000).toISOString(),
          url: "https://theverge.com",
          image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800"
        }
      ]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNews();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredNews = news.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-purple-200 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Newspaper className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">AI News</h1>
              <p className="text-purple-200 mt-1">Stay updated with the latest in artificial intelligence</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-6 text-sm text-purple-200">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
            <button 
              onClick={fetchNews}
              className="flex items-center gap-1 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="sticky top-0 bg-white dark:bg-slate-800 shadow-sm z-40 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search AI news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Categories */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === category.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* News Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No news found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {filteredNews.length > 0 && (
              <a
                href={filteredNews[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-12 group"
              >
                <div className="relative h-96 rounded-3xl overflow-hidden">
                  <img
                    src={filteredNews[0].image}
                    alt={filteredNews[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <Badge className="bg-purple-600 text-white mb-4">Featured</Badge>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                      {filteredNews[0].title}
                    </h2>
                    <p className="text-slate-300 text-lg mb-4 line-clamp-2">{filteredNews[0].description}</p>
                    <div className="flex items-center gap-4 text-slate-400">
                      <span>{filteredNews[0].source}</span>
                      <span>•</span>
                      <span>{formatDate(filteredNews[0].publishedAt)}</span>
                    </div>
                  </div>
                </div>
              </a>
            )}

            {/* News Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNews.slice(1).map((article, idx) => (
                <a
                  key={article.id || idx}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 dark:border-slate-700"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <Badge className="bg-white/90 text-slate-700 text-xs">
                        {article.source}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-3">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(article.publishedAt)}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4">
                      {article.description}
                    </p>
                    <div className="flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium">
                      Read More
                      <ExternalLink className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Footer CTA */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want AI Solutions for Your Business?</h2>
          <p className="text-purple-200 mb-8">Let us help you leverage the power of artificial intelligence</p>
          <Link to="/#contact">
            <Button className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-3 rounded-full text-lg font-semibold">
              Talk to Our AI Experts
            </Button>
          </Link>
        </div>
      </section>
      
      <WebsiteFooter />
    </div>
  );
}
